import React, { useState, useEffect } from "react";
import {
  Card,
  Button,
  Space,
  Tag,
  message,
  Breadcrumb,
  Divider,
  Row,
  Col,
  Input,
  Select,
  Spin,
  Image,
  Form,
  Avatar,
  Typography,
} from "antd";
import {
  HomeOutlined,
  CommentOutlined,
  ArrowLeftOutlined,
  SaveOutlined,
  UserOutlined,
  ClockCircleOutlined,
  CheckCircleOutlined,
} from "@ant-design/icons";
import { useNavigate, useParams } from "react-router-dom";
import { reflectionAPIWithErrorHandling, reflectionHelpers } from "../../../api/reflection/reflection";
import dayjs from "dayjs";

const { Title, Text, Paragraph } = Typography;

export default function ReflectionDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form] = Form.useForm();
  const [reflection, setReflection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    fetchDetail();
  }, [id]);

  const fetchDetail = async () => {
    setLoading(true);
    try {
      const response = await reflectionAPIWithErrorHandling.getReflectionById(id);
      const data = reflectionHelpers.extractReflectionData(response);
      setReflection(data);
      form.setFieldsValue({
        response: data.response,
        status: data.status === 'received' ? 'processing' : data.status,
      });
    } catch (error) {
      message.error(error.message || "Không thể tải chi tiết phản ánh");
      navigate("/reflections");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (values) => {
    setSubmitting(true);
    try {
      await reflectionAPIWithErrorHandling.replyReflection(id, values);
      message.success("Đã gửi phản hồi thành công!");
      fetchDetail();
    } catch (error) {
      message.error(error.message || "Lỗi khi gửi phản hồi");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) return <div className="p-12 text-center"><Spin size="large" /></div>;
  if (!reflection) return null;

  return (
    <div className="bg-gray-50 min-h-screen p-6">
      <Card className="shadow-sm border-none">
        <Breadcrumb
          separator=">"
          className="mb-4"
          items={[
            { href: "/", title: <><HomeOutlined /> Trang chủ</> },
            { href: "/reflections", title: <><CommentOutlined /> Phản ánh</> },
            { title: "Chi tiết phản ánh" },
          ]}
        />
        <Divider />

        <Row gutter={24}>
          <Col xs={24} lg={15}>
            <Card className="mb-6 elevation-1 border-none shadow-sm">
              <div className="flex justify-between items-start mb-4">
                <Title level={4} className="m-0 text-blue-800">{reflection.title}</Title>
                <Tag color={reflection.status === 'completed' ? 'green' : 'blue'}>
                  {reflection.status === 'received' ? 'Mới nhận' : reflection.status === 'processing' ? 'Đang xử lý' : 'Đã hoàn thành'}
                </Tag>
              </div>
              
              <Space className="mb-6 text-gray-500 text-sm" split={<Divider type="vertical" />}>
                <span><ClockCircleOutlined /> {dayjs(reflection.created_at).format("DD/MM/YYYY HH:mm")}</span>
                <span>Danh mục: <Tag size="small">{reflection.category || "N/A"}</Tag></span>
              </Space>

              <div className="bg-blue-50 p-4 rounded-lg mb-6">
                <Paragraph className="text-gray-700 whitespace-pre-wrap m-0">
                  {reflection.content}
                </Paragraph>
              </div>

              {reflection.images && reflection.images.length > 0 && (
                <div className="mb-6">
                  <Text strong className="block mb-2">Hình ảnh đính kèm:</Text>
                  <Space size={[8, 8]} wrap>
                    {reflection.images.map(img => (
                      <Image 
                        key={img.id} 
                        src={img.url} 
                        width={150} 
                        height={100} 
                        className="rounded-md object-cover cursor-pointer border shadow-sm"
                        preview={{ mask: 'Xem ảnh' }}
                      />
                    ))}
                  </Space>
                </div>
              )}
            </Card>

            <Card title={<><CommentOutlined /> Phản hồi từ cán bộ</>} className="shadow-sm border-none">
              <Form
                form={form}
                layout="vertical"
                onFinish={handleSubmit}
              >
                <Form.Item
                  name="response"
                  label="Nội dung phản hồi"
                  rules={[{ required: true, message: "Vui lòng nhập nội dung phản hồi!" }]}
                >
                  <Input.TextArea rows={6} placeholder="Nhập nội dung phản hồi cho cư dân..." />
                </Form.Item>

                <Form.Item
                  name="status"
                  label="Trạng thái xử lý"
                  rules={[{ required: true }]}
                >
                  <Select>
                    <Select.Option value="processing">Đang xử lý</Select.Option>
                    <Select.Option value="completed">Đã hoàn thành</Select.Option>
                  </Select>
                </Form.Item>

                <div className="flex justify-end pt-4">
                  <Button 
                    type="primary" 
                    htmlType="submit" 
                    icon={<SaveOutlined />}
                    loading={submitting}
                    className="min-w-40"
                  >
                    Gửi phản hồi
                  </Button>
                </div>
              </Form>

              {reflection.responder && (
                <div className="mt-8 pt-6 border-t border-gray-100 italic text-gray-400 text-xs text-right">
                  Phản hồi lần cuối bởi {reflection.responder.name} lúc {dayjs(reflection.response_at).format("DD/MM/YYYY HH:mm")}
                </div>
              )}
            </Card>
          </Col>

          <Col xs={24} lg={9}>
            <Card title="Thông tin cư dân" className="shadow-sm border-none mb-6">
              <div className="flex flex-col items-center py-4">
                <Avatar size={80} icon={<UserOutlined />} className="bg-blue-100 text-blue-500 mb-4" />
                <Title level={5} className="mb-1 flex items-center gap-2">
                  {reflection.customer?.name || "Khách vãng lai"}
                  {reflection.customer_id ? (
                    <Tag color="processing" className="m-0 text-[10px]">Cư dân</Tag>
                  ) : (
                    <Tag color="default" className="m-0 text-[10px]">Vãng lai</Tag>
                  )}
                </Title>
                <Text type="secondary">{reflection.customer?.phone || reflection.phone || "N/A"}</Text>
              </div>
              <Divider className="my-2" />
              <div className="space-y-3 py-2">
                <div className="flex justify-between">
                  <Text type="secondary">ID tài khoản:</Text>
                  <Text strong>{reflection.user?.id}</Text>
                </div>
                {/* Có thể thêm các thông tin khác về cư dân nếu có trong user object */}
              </div>
            </Card>

            <Button 
              block 
              icon={<ArrowLeftOutlined />} 
              onClick={() => navigate("/reflections")}
              className="mt-4"
            >
              Quay lại danh sách
            </Button>
          </Col>
        </Row>
      </Card>
    </div>
  );
}
